import React, { Dispatch, useEffect, useRef } from 'react'
import { ThemeTypes } from '../../context/ContextTypes'
import { useTheme } from '../../context/ThemeContext'
import OLMap from 'ol/Map'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { Cluster } from 'ol/source'
import { Style, Fill, Stroke, Text, Circle } from 'ol/style'
import GeoJSON from 'ol/format/GeoJSON'
import { Feature, Overlay, View } from 'ol'
import { Point } from 'ol/geom'
import { fromLonLat } from 'ol/proj'
import { defaults as defaultControls } from 'ol/control'
import { APIResponseAcknowledgement } from './APIAcknowledgeService'

type ThemeSeaColorTypes = {
    theme: ThemeTypes
    seaColor: string
    borderColor: string
    borderWidth: number
    backgroundFill: string
}

const themeSeaColor: ThemeSeaColorTypes[] = [
    { theme: 'dark', seaColor: '#080831', borderColor: '#7ba6f5ff', borderWidth: 1, backgroundFill: '#535897ff' },
    { theme: 'light', seaColor: '#e4e4ffff', borderColor: '#7ba6f5ff', borderWidth: 1, backgroundFill: '#7d7fa0ff' },
    { theme: 'colorful', seaColor: '#b3d6f3ff', borderColor: '#7ba6f5ff', borderWidth: 1, backgroundFill: '#aeb2f3ff' }
]

type DashboardMapPropTypes = {
    gridArea?: string
    locationData: APIResponseAcknowledgement[]
    setLocationData: Dispatch<React.SetStateAction<APIResponseAcknowledgement[]>>
}

const AcknowledgementMap = (props: DashboardMapPropTypes) => {
    const { theme } = useTheme()

    const mapRef = useRef<HTMLDivElement>(null)
    const mapObjRef = useRef<OLMap | null>(null)

    /** Cluster layers per warning ID */
    const clusterLayerMapRef = useRef<Map<string, VectorLayer>>(new Map())
    const warningColorMapRef = useRef<Map<string, string>>(new Map())

    /** Tooltip */
    const tooltipRef = useRef<HTMLDivElement>(null)
    const tooltipOverlayRef = useRef<Overlay | null>(null)

    /** ---------------- MAP INIT ---------------- */
    useEffect(() => {
        if (!mapRef.current || mapObjRef.current) return

        const adm1 = new VectorLayer({
            source: new VectorSource({ url: '/geoJSON/maldives/maldives_adm_1.json', format: new GeoJSON() }),
            style: () => {
                const fill = themeSeaColor.find(t => t.theme === theme)
                return new Style({
                    fill: new Fill({ color: fill?.backgroundFill }),
                    stroke: new Stroke({ color: fill?.borderColor, width: fill?.borderWidth })
                })
            },
            zIndex: 5
        })

        const adm2 = new VectorLayer({
            source: new VectorSource({ url: '/geoJSON/maldives/maldives_adm_2.json', format: new GeoJSON() }),
            style: () => {
                const fill = themeSeaColor.find(t => t.theme === theme)
                return new Style({
                    fill: new Fill({ color: fill?.backgroundFill }),
                    stroke: new Stroke({ color: fill?.borderColor, width: fill?.borderWidth })
                })
            },
            zIndex: 10
        })

        const map = new OLMap({
            target: mapRef.current,
            layers: [adm1, adm2],
            view: new View({
                center: fromLonLat([73.2207, 3.2028]),
                zoom: 6
            }),
            controls: defaultControls({ zoom: false, attribution: true })
        })

        /** Tooltip */
        if (tooltipRef.current) {
            tooltipOverlayRef.current = new Overlay({
                element: tooltipRef.current,
                offset: [10, 10],
                positioning: 'bottom-left'
            })
            map.addOverlay(tooltipOverlayRef.current)

            map.on('pointermove', evt => {
                const feature = map.forEachFeatureAtPixel(evt.pixel, f => f)
                if (!feature) {
                    tooltipRef.current!.style.display = 'none'
                    return
                }

                const clustered = feature.get('features')
                if (!clustered) {
                    tooltipRef.current!.style.display = 'none'
                    return
                }

                const ewId = clustered[0]?.get('ewId')

                tooltipRef.current!.innerHTML = `
                    <div><strong>Warning ID:</strong> ${ewId}</div>
                    <div><strong>Count:</strong> ${clustered.length}</div>
                `
                tooltipOverlayRef.current!.setPosition(evt.coordinate)
                tooltipRef.current!.style.display = 'block'
            })
        }

        mapObjRef.current = map
    }, [])

    /** ---------------- CLUSTER BY SAME ID ONLY ---------------- */
    useEffect(() => {
        if (!mapObjRef.current) return
        const map = mapObjRef.current

        /** Remove old layers */
        clusterLayerMapRef.current.forEach(layer => map.removeLayer(layer))
        clusterLayerMapRef.current.clear()

        /** Group data by ewId */
        const grouped = new Map<string, APIResponseAcknowledgement[]>()
        props.locationData.forEach(item => {
            if (!grouped.has(item.ewId)) grouped.set(item.ewId, [])
            grouped.get(item.ewId)!.push(item)
        })

        grouped.forEach((items, ewId) => {
            if (!warningColorMapRef.current.has(ewId)) {
                warningColorMapRef.current.set(
                    ewId,
                    `hsl(${Math.floor(Math.random() * 360)}, 70%, 50%)`
                )
            }

            const color = warningColorMapRef.current.get(ewId)!

            const source = new VectorSource()
            items.forEach(item => {
                source.addFeature(
                    new Feature({
                        geometry: new Point(fromLonLat([item.lon, item.lat])),
                        ewId
                    })
                )
            })

            const clusterSource = new Cluster({
                distance: 40,
                source
            })

            const layer = new VectorLayer({
                source: clusterSource,
                zIndex: 1000,
                style: feature => {
                    const size = feature.get('features').length
                    return new Style({
                        image: new Circle({
                            radius: 15,
                            fill: new Fill({ color }),
                            stroke: new Stroke({ color: '#fff', width: 2 })
                        }),
                        text: new Text({
                            text: size.toString(),
                            fill: new Fill({ color: '#fff' }),
                            font: 'bold 12px sans-serif'
                        })
                    })
                }
            })

            map.addLayer(layer)
            clusterLayerMapRef.current.set(ewId, layer)
        })
    }, [props.locationData])

    /** ---------------- THEME ---------------- */
    useEffect(() => {
        const t = themeSeaColor.find(x => x.theme === theme)
        if (t && mapRef.current) mapRef.current.style.backgroundColor = t.seaColor
    }, [theme])

    return (
        <div className="dashboard" style={{ gridArea: props.gridArea || '' }}>
            <div className="dashboard-map" style={{ position: 'relative' }}>
                <div ref={tooltipRef} className="ol-tooltip" />
                <div ref={mapRef} style={{ width: '100%', height: '100%', borderRadius: '0.5rem' }} />
            </div>
        </div>
    )
}

export default AcknowledgementMap
